import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'Xentrios Cloud Dental PMS',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Cloud-native dental PMS for U.S. multi-location DSOs. AI-enabled, natively integrated with RevTech RCM analytics and NDS DataVault. Built for DSOs from day one — not a legacy system retrofitted for groups.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'Xentrios',
        url: 'https://xentrios.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a cloud dental PMS?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A cloud dental PMS (Practice Management System) is dental management software that runs entirely in a web browser via cloud infrastructure — no local servers, no installed software, and no VPN required. All scheduling, clinical documentation, billing, claims processing, and reporting happen through a browser on any device. For multi-location DSOs, cloud PMS means all locations access the same system simultaneously, enterprise reporting is available in real time, and IT overhead for server maintenance and software updates is eliminated.',
          },
        },
        {
          '@type': 'Question',
          name: 'How is Xentrios different from legacy dental PMS systems?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Legacy dental PMS systems like Dentrix and Eaglesoft were designed for single-practice environments in the 1990s and early 2000s and have been incrementally updated since. They require local server infrastructure, IT maintenance, location-by-location installations, and separate third-party tools for analytics and RCM. Xentrios is built as a cloud-native, multi-tenant platform from the ground up — with enterprise reporting, AI workflow assistance, and the full RevTech + NDS analytics stack built in, not bolted on.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does Xentrios include RCM tools or do we need to buy those separately?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Xentrios ships with native integration to the full RevTech RCM suite — InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ — and to the NDS DataVault data warehouse. These are not add-on modules requiring separate contracts; they are built into the Xentrios platform architecture. Your AR team can work denials in DenialIQ and your leadership can view enterprise AR analytics in InsightFlow from within the same system your clinical staff uses for scheduling and charting.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can Xentrios support a DSO with multiple practice management systems?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. For DSOs with existing locations on Dentrix, Eaglesoft, Open Dental, CareStack, or Planet DDS, the NDS DataVault layer provides data normalization so Xentrios analytics and RevTech tools can see data from all systems in one view — even before a full PMS migration. For DSOs migrating to Xentrios over time, a phased approach allows new locations or acquisitions to go live on Xentrios while legacy locations continue on their existing PMS without losing enterprise reporting continuity.',
          },
        },
      ],
    },
  ],
};

const modules = [
  {
    icon: '📅',
    name: 'Scheduling',
    desc: 'Multi-provider, multi-operatory appointment management with drag-and-drop interface, automated reminders, recall management, and real-time availability across all locations. Intelligent conflict detection and same-day capacity optimization.',
  },
  {
    icon: '🦷',
    name: 'Clinical',
    desc: 'Full clinical charting with perio, restorative, and orthodontic workflows. AI-assisted documentation, treatment planning with patient presentation mode, medical history with contraindication alerts, and imaging system integration.',
  },
  {
    icon: '💰',
    name: 'Billing',
    desc: 'Patient billing, payment plan management, credit card and ACH processing, statement generation, and automated collections workflows. Real-time AR aging with configurable follow-up sequences.',
  },
  {
    icon: '📋',
    name: 'Claims',
    desc: 'Electronic claims submission, ERA/EOB auto-posting, real-time status tracking, and ClaimVision pre-submission intelligence. Batch processing for high-volume practices with automatic exception flagging.',
  },
  {
    icon: '✅',
    name: 'Pre-Auth & Appeals',
    desc: 'Full pre-authorization lifecycle management with clinical documentation attachment and status tracking. Appeal workflows integrated with DenialIQ — denials generated in the PMS surface automatically in the denial management workspace.',
  },
  {
    icon: '📊',
    name: 'Reports',
    desc: 'Built-in production, collections, scheduling, and clinical reports — plus the full InsightFlow analytics suite with enterprise roll-up across all locations. Power BI integration through NDS DataVault for custom KPI dashboards.',
  },
  {
    icon: '🏢',
    name: 'Multi-Location',
    desc: 'Enterprise controls with practice-level flexibility. Centralized fee schedule management, standardized clinical protocols, cross-location provider scheduling, consolidated billing, and location-level P&L reporting.',
  },
  {
    icon: '💬',
    name: 'Communications',
    desc: 'Integrated patient communications: automated appointment reminders, recall campaigns, post-visit follow-up, two-way SMS, patient portal, and a complete communication log per patient across all channels.',
  },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'NDS DataVault'];

export default function CloudDentalPMSPage() {
  const cmsPage = useCmsPage('xentrios', 'cloud-dental-pms');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Cloud Dental PMS for Multi-Location DSOs');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const moduleItems = c(cmsPage, 'modules', 'items', modules);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>Cloud Dental PMS for Multi-Location DSOs</title>
        <meta
          name="description"
          content="Xentrios is a cloud-native dental PMS built for U.S. multi-location DSOs. AI-enabled, natively integrated with RevTech RCM analytics and NDS DataVault. Not a legacy system retrofitted for groups — built for DSOs from day one."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Cloud Dental PMS</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/features"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See All Features
            </Link>
          </div>
        </div>
      </section>

      {/* Why cloud-native matters */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Why It Matters</p>
          <h2>Why cloud-native matters for DSOs</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Cloud-native isn't just a marketing term. For a multi-location dental group, the architecture
            of your PMS determines what's operationally possible — and what isn't.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div>
              <h3 style={{ color: '#c62828', marginBottom: '1rem' }}>Legacy PMS problems at scale</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.9, color: '#444' }}>
                <li>
                  <strong>Server-per-location architecture</strong> — each practice requires its own server,
                  maintenance contract, and IT support relationship
                </li>
                <li>
                  <strong>No real-time enterprise visibility</strong> — pulling a cross-location production
                  report requires either manual exports or a separate data aggregation tool
                </li>
                <li>
                  <strong>Update complexity</strong> — software updates must be deployed to every location
                  separately, often requiring downtime and IT coordination
                </li>
                <li>
                  <strong>Acquisition friction</strong> — each new location comes with its own PMS
                  installation, database, and configuration — multiplying IT complexity with every acquisition
                </li>
                <li>
                  <strong>Remote access workarounds</strong> — cloud access to a server-based PMS typically
                  requires VPN or Citrix, both of which add latency and IT overhead
                </li>
                <li>
                  <strong>RCM disconnection</strong> — AR analytics, denial management, and payment
                  reconciliation require separate tools that pull data from the PMS manually
                </li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: '#388e3c', marginBottom: '1rem' }}>Xentrios cloud-native advantages</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.9, color: '#444' }}>
                <li>
                  <strong>Zero server infrastructure</strong> — no hardware to procure, maintain, or replace.
                  Xentrios runs in your browser on any device at any location
                </li>
                <li>
                  <strong>Real-time enterprise reporting</strong> — production, AR, scheduling, and clinical
                  metrics across all locations in a single view, updated live
                </li>
                <li>
                  <strong>Instant updates</strong> — platform updates deploy centrally with no location-level
                  IT involvement and no downtime
                </li>
                <li>
                  <strong>Acquisition-ready architecture</strong> — a new location goes live on Xentrios in
                  days, not weeks. The platform scales without increasing infrastructure complexity
                </li>
                <li>
                  <strong>Native AI throughout</strong> — documentation assistance, coding suggestions,
                  eligibility pre-screening, and scheduling optimization are embedded in every workflow
                </li>
                <li>
                  <strong>RevTech and NDS built in</strong> — AR analytics, denial management, reconciliation,
                  and the data warehouse are part of the platform — not separate integrations to manage
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Module grid */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Platform Modules</p>
          <h2>Every workflow your DSO needs — one system.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Xentrios covers the complete practice management workflow from scheduling through collections,
            with AI assistance at every step and enterprise controls across every location.
          </p>
          <div className="xen-modules">
            {moduleItems.map((m) => (
              <div key={m.name} className="xen-module">
                <div className="xen-module-icon">{m.icon}</div>
                <div>
                  <h4>{m.name}</h4>
                  <p>{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RevTech and NDS built in */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <p className="xen-section-eyebrow" style={{ color: 'var(--xen-accent-light)' }}>
            The Stack, Built In
          </p>
          <h2>RevTech and NDS come with Xentrios.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2.5rem' }}>
            Most PMS vendors sell you a scheduling and billing system and leave you to figure out analytics,
            RCM, and data infrastructure on your own. Xentrios ships with the full stack.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginTop: '1.5rem',
            }}
          >
            {[
              {
                brand: 'RevTech Partners',
                color: '#388e3c',
                items: [
                  'InsightFlow — enterprise AR analytics',
                  'DenialIQ — denial management workspace',
                  'ClaimVision — pre-submission intelligence',
                  'PayerIQ — payer contract analytics',
                  'ReconIQ — payment reconciliation',
                ],
              },
              {
                brand: 'NDS DataVault',
                color: '#1976d2',
                items: [
                  'Multi-PMS data extraction',
                  'Data normalization and governance',
                  'Power BI report packages',
                  'Historical trend analytics',
                  'Custom KPI model development',
                ],
              },
              {
                brand: 'MaximizeRCM',
                color: '#e65100',
                items: [
                  'Billing team training and consulting',
                  'Payer contract negotiation support',
                  'RCM workflow advisory',
                  'Implementation and go-live support',
                  'Ongoing operational consulting',
                ],
              },
            ].map((col) => (
              <div
                key={col.brand}
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  borderTop: `4px solid ${col.color}`,
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: '#fff',
                    marginBottom: '1rem',
                  }}
                >
                  {col.brand}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {col.items.map((item) => (
                    <li
                      key={item}
                      style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}
                    >
                      <span style={{ color: col.color, fontWeight: 700, marginTop: '1px', flexShrink: 0 }}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration badges + migration note */}
      <section className="xen-section xen-section-center xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Integrations & Migration</p>
          <h2>Connect your existing systems. Migrate on your timeline.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Xentrios natively integrates with legacy PMS systems through the NDS DataVault layer — so
            enterprise analytics work from day one, even before a full migration.
          </p>
          <div className="xen-integrations" style={{ marginBottom: '2rem' }}>
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.5rem 2rem',
              maxWidth: '680px',
              margin: '0 auto',
              borderLeft: '4px solid var(--xen-accent)',
              textAlign: 'left',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            }}
          >
            <p style={{ fontWeight: 600, color: 'var(--xen-navy)', marginBottom: '0.5rem' }}>
              On PMS migration
            </p>
            <p style={{ color: '#555', lineHeight: 1.7 }}>
              For DSOs that want to adopt the full Xentrios stack, we support phased migrations — new
              locations and acquisitions go live on Xentrios while existing locations continue on their
              current PMS. The NDS DataVault layer provides unified reporting across both environments
              throughout the transition. No "big bang" cutover required, and no loss of historical data
              or reporting continuity during the migration period.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See Xentrios for your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Tell us how many locations you operate and which PMS you're currently using. We'll show you
            what the Xentrios platform looks like for your specific organization — including how RevTech
            and NDS analytics work alongside your existing infrastructure.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
